import type { RouteRecordNameGeneric } from 'vue-router';
import {
  CARD_EXTERNAL_USER_ALLOWED_ROUTES,
  FINISHED_REGISTRATION_KYC_STAGES,
  OPEN_ONBOARDING_ALLOWED_ACTIONS,
} from '../constants/exclusion.constant';
import { EXTERNAL_USER_LEVEL } from '@/shared/common/constants/userLevels.constant';
import { shouldShowSelfManagedOnboarding } from '@/helpers/kyc';
import type { KycCompliance } from '@/types/KycCompliance';
import type { AccessRolAction, AccessRolSection } from '../types/accessRol';

/** Onboarding abierto: registro sin enviar u onboarding autogestionado en curso. */
export function hasOpenOnboarding(kycStatus: KycCompliance): boolean {
  if (!kycStatus?.company?.id) return false;

  const hasFinishedRegistration =
    FINISHED_REGISTRATION_KYC_STAGES.includes(kycStatus.company.kycStage1) &&
    FINISHED_REGISTRATION_KYC_STAGES.includes(kycStatus.b2bUser?.kycStage1);

  return !hasFinishedRegistration || shouldShowSelfManagedOnboarding(kycStatus);
}

/** Con el onboarding abierto, bloquea toda acción de la sección que no esté entre las permitidas. */
export function openOnboardingActionExclusion({
  isOnboardingOpen,
  section,
  action,
}: {
  isOnboardingOpen: boolean;
  section: AccessRolSection;
  action: AccessRolAction;
}): boolean {
  if (!isOnboardingOpen) return false;

  const allowedActions = OPEN_ONBOARDING_ALLOWED_ACTIONS[section];

  if (!allowedActions) return false;

  return !allowedActions.includes(action);
}

export const cardExternalUserExclusion = ({
  level,
  section,
}: {
  level: number;
  section?: string;
}): boolean => {
  if (level !== EXTERNAL_USER_LEVEL) return false;

  if (section) {
    const onlySectionAllowed = 'CARD';

    return section !== onlySectionAllowed;
  }

  return true;
};

export const cardExternalUserRouteExclusion = ({
  level,
  fromRouteName,
  toRouteName,
}: {
  level: number;
  fromRouteName: RouteRecordNameGeneric | null;
  toRouteName: RouteRecordNameGeneric | null;
}): {
  isExcluded: boolean;
  redirectRouteName: RouteRecordNameGeneric | null;
} => {
  const response = {
    isExcluded: false,
    redirectRouteName: null as RouteRecordNameGeneric | null,
  };

  if (cardExternalUserExclusion({ level })) {
    response.isExcluded = !CARD_EXTERNAL_USER_ALLOWED_ROUTES.includes(
      String(toRouteName)
    );

    const isValidOriginRoute = CARD_EXTERNAL_USER_ALLOWED_ROUTES.includes(
      String(fromRouteName)
    );

    if (response.isExcluded) {
      response.redirectRouteName = isValidOriginRoute ? fromRouteName : 'card';
    }
    return response;
  }

  return response;
};
